import { useCurrentKey } from '#hooks/PRIVATE_useCurrentKey';
import {
  getFirstVisibleKey,
  getItemKeysArray,
  getPrevNextKeyUsingRef,
  keyExtractor
} from '#utils/PRIVATE_collectionUtils';
import { useEffect, useId, useRef, useState, useMemo } from 'preact/hooks';
import { CollapsibleNavigationListContext } from './CollapsibleNavigationListContext';
import { CollapsibleNavigationListItem } from './CollapsibleNavigationListItem';
import { mergeProps } from '#utils/UNSAFE_mergeProps';
import { useCollectionFocusRing } from '#hooks/PRIVATE_useCollectionFocusRing';
import { classNames } from '#utils/UNSAFE_classNames';
import { navigationListStyles } from './themes/CollapsibleNavigationListStyles.css';

type Item<K> = {
  itemKey: K;
  label: string;
  children: Item<K>;
};
type SelectionDetail<K> = {
  value: K;
};

type ToggleDetail<K> = {
  value: K;
};
export type CollapsibleNavigationListProps<K> = {
  items: any;
  selection?: K | undefined;
  expandedKeys?: Set<K>;
  edge?: 'start' | 'end';
  onToggle?: (detail: ToggleDetail<K>) => void;
  onSelect?: <K extends string | number>(detail: SelectionDetail<K>) => void;
};
export const CollapsibleNavigationList = <K extends string | number>({
  items,
  selection,
  expandedKeys,
  edge,
  onToggle,
  onSelect
}: CollapsibleNavigationListProps<K>) => {
  const groupKeys = useMemo(() => getGroupKeys(items), [items]);
  const rootRef = useRef<HTMLDivElement>(null);

  const [showFocusRing, focusRingProps] = useCollectionFocusRing(rootRef, [
    'ArrowUp',
    'ArrowDown',
    'Home',
    'End'
  ]);

  const onFocus = () => {
    if (rootRef.current && currentKey === undefined) {
      const key = getFirstVisibleKey(rootRef.current, ITEM_SELECTOR);
      if (key != null) {
        setCurrentKey(key as K);
      }
    }
  };
  const onKeyDown = (event: KeyboardEvent) => {
    if (rootRef.current && currentKey != null) {
      if (event.key === 'Home' || event.key === 'End') {
        const tabBarItemKey = getItemKeysArray(rootRef.current, ITEM_SELECTOR);
        event.preventDefault();
        setCurrentKey?.(tabBarItemKey[event.key === 'Home' ? 0 : tabBarItemKey.length - 1] as K);
      }
      //Enter or space expands a collapsed node, collapses an expanded node and also triggers onSelect
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onSelect?.({ value: currentKey });
        if (groupKeys.has(currentKey as K)) {
          onToggle?.({ value: currentKey });
        }
      }
      //ArrowRight only expands, if detected on a previously expanded node it remains expanded
      if (event.key === 'ArrowRight') {
        if (
          groupKeys.has(currentKey as K) &&
          !(expandedKeys && expandedKeys.has(currentKey as K))
        ) {
          onToggle?.({ value: currentKey });
        }
      }

      //ArrowLeft only collapses, if detected on a previously collapsed node it remains collapsed
      if (event.key === 'ArrowLeft') {
        if (groupKeys.has(currentKey as K) && expandedKeys && expandedKeys.has(currentKey as K)) {
          onToggle?.({ value: currentKey });
        }
      }
    }
  };
  const [currentKey, setCurrentKey] = useState<K | undefined>(selection as K);
  const ITEM_SELECTOR = '[role="treeitem"]';
  const { currentKeyProps } = useCurrentKey(
    (element) => {
      const key = keyExtractor(element, ITEM_SELECTOR);
      return key;
    },
    false,
    false,
    getPrevNextKeyUsingRef(rootRef, currentKey, true, ITEM_SELECTOR),
    getPrevNextKeyUsingRef(rootRef, currentKey, false, ITEM_SELECTOR),
    undefined,
    undefined,
    currentKey,
    (detail) => {
      const key = detail.value as K;
      setCurrentKey(key);
    }
  );

  const navListId = useId();
  const navigationListItemPrefix = navListId + '_';

  const collapsibleNavigationListContext = {
    selection: selection as string | number | undefined,
    onSelect: onSelect as ((detail: { value: string | number }) => void) | undefined,
    onToggle: onToggle as ((detail: { value: string | number }) => void) | undefined,
    expandedKeys,
    edge,
    showFocusRing,
    currentKey: currentKey,
    navigationListItemPrefix
  };

  useEffect(() => {
    if (rootRef.current) {
      const itemKeys = getItemKeysArray(rootRef.current, ITEM_SELECTOR);
      if (currentKey == null && itemKeys) {
        if (selection != null && itemKeys.includes(selection as K)) {
          setCurrentKey(selection as K);
        } else if (itemKeys.length > 0) {
          setCurrentKey(itemKeys[0] as K);
        }
      }
    }
  }, [items, currentKey, rootRef, selection]);

  const eventProps = { onKeyDown, onFocus };
  //Styling
  const navigationListClass = classNames([navigationListStyles.uListStyle]);
  return (
    <CollapsibleNavigationListContext.Provider value={collapsibleNavigationListContext}>
      <div
        role="tree"
        class={navigationListClass}
        ref={rootRef}
        {...mergeProps(currentKeyProps, focusRingProps, eventProps)}
        tabindex={0}
        aria-activedescendant={currentKey ? navigationListItemPrefix : ''}>
        {items.map((item: any) => (
          <CollapsibleNavigationListItem
            itemKey={item.itemKey}
            item={item}
            expandedKeys={expandedKeys}
            level={0}
            label={item.label}
            onToggle={onToggle!}
          />
        ))}
      </div>
    </CollapsibleNavigationListContext.Provider>
  );
};

// Helper to extract all group item keys from items tree
const getGroupKeys = <K extends string | number>(items: Item<K>[]) => {
  const groupKeys = new Set();
  const visited = new Set();
  const keysArray = [...items];
  while (keysArray.length) {
    const item = keysArray.pop();
    if (visited.has(item?.itemKey)) continue;
    visited.add(item?.itemKey);
    if (Array.isArray(item?.children) && item?.children.length > 0) {
      groupKeys.add(item.itemKey);
      keysArray.push(...item.children);
    }
  }
  return groupKeys;
};
